import _ from 'underscore';

import {insertBlockItemOnEnterKey} from 'notebook/mediocre/base-plugin/insertBlockItemOnEnterKey';
import {ItemInsertOptions} from 'notebook/mediocre/EditorTypes';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
import MultiSelectList, {
  MultiSelectListComponent,
  MultiSelectListProps,
} from 'notebook/mediocre/selection/block/MultiSelectListMixin';
import {MultiSelectState} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import * as keyboard from 'util/Keyboard';

// Defines the component type that uses this mixin
interface MultiSelectLinearListComponent<
  TIndex,
  P extends MultiSelectListProps<TIndex> = MultiSelectListProps<TIndex>,
  S extends MultiSelectState<TIndex> = MultiSelectState<TIndex>,
> extends MultiSelectListComponent<TIndex, P, S> {
  // Methods and fields that must be provided by consumer
  deleteRangeHandler: (args: {start: TIndex; end: TIndex}) => void;
  inputRangeHandler: (args: {start: TIndex; end: TIndex; text: string}) => void;
  tabRangeHandler: (args: {start: TIndex; end: TIndex; shiftKey: boolean}) => void;
  pasteRangeHandler: (args: {start: TIndex; end: TIndex; originalEvent: ClipboardEvent}) => void;
  // Methods and fields that the constructor doesn't SAY must be provided by consumer, but which nevertheless
  // must
  formatRangeHandler: (args: {start: TIndex; end: TIndex; format: string}) => void;
  insertItemHandler: (index: TIndex, type?: string | null, options?: ItemInsertOptions) => void;
  saveMultiSelect: () => void;
  setSelection: (args: {start: TIndex; end: TIndex} | null) => void;
}

// eslint-disable-next-line no-restricted-syntax
const MultiSelectLinearList = _.extend({}, MultiSelectList, {
  // Components should also implement:
  // - tabRangeHandler
  // - deleteRangeHandler
  // - inputRangeHandler
  // - pasteRangeHandler: this method is responsible for calling preventDefault when pasting, which allows
  //                      the component to handle a FF bug. See: wyciwyp.extractImageFromPaste

  isIndexBetween<TIndex>(
    this: MultiSelectLinearListComponent<TIndex>,
    startIndex: TIndex,
    endIndex: TIndex,
    index: TIndex
  ): boolean {
    if (this.compareIndex(startIndex, endIndex) > 0) {
      [startIndex, endIndex] = [endIndex, startIndex];
    }
    return this.compareIndex(startIndex, index) <= 0 && this.compareIndex(index, endIndex) <= 0;
  },

  allowSingleMultiSelect<TIndex>(this: MultiSelectLinearListComponent<TIndex>): boolean {
    return false;
  },

  multiInputHandler<TIndex>(
    this: MultiSelectLinearListComponent<TIndex>,
    {index, text}: {index?: TIndex; text: string}
  ): void {
    const range = this.sortedMultiRange();
    if (this.inputRangeHandler == null || range == null) {
      return;
    }
    this.saveMultiSelect();
    const {start, end} = range;
    if (start === end && text !== '') {
      // If only 1 item is selected and we're not performing a cut, don't replace it
      return;
    }
    this.inputRangeHandler({start, end, text});
  },

  multiPasteHandler<TIndex>(this: MultiSelectLinearListComponent<TIndex>, e: ClipboardEvent) {
    const range = this.sortedMultiRange();
    if (this.pasteRangeHandler == null || range == null) {
      e.preventDefault();
      return;
    }
    this.saveMultiSelect();
    const {start, end} = range;
    this.pasteRangeHandler({start, end, originalEvent: e});
  },

  multiArrowSelectHandler<TIndex>(
    this: MultiSelectLinearListComponent<TIndex>,
    index: TIndex,
    e: {up: boolean}
  ): void {
    const {up} = e;
    if (up) {
      this.setMultiIndices({
        start: index,
        end: this.previousIndex(index),
      });
    } else {
      this.setMultiIndices({
        start: index,
        end: this.nextIndex(index),
      });
    }
    // Clear the selection
    this.setSelection(null);
  },

  multiKeyDownHandler<TIndex>(this: MultiSelectLinearListComponent<TIndex>, e: KeyboardEvent): void {
    let end;
    let start;
    let targetIndex;
    if (keyboard.isTabKey(e)) {
      e.preventDefault();
      if (this.tabRangeHandler != null && !this.isLockedWithEditWarn()) {
        this.saveMultiSelect();
        ({start, end} = this.sortedMultiRange()!);
        this.tabRangeHandler({
          start,
          end,
          shiftKey: e.shiftKey,
        });
      }
    } else if (keyboard.isADeleteKey(e)) {
      e.preventDefault();
      if (this.deleteRangeHandler != null && !this.isLockedWithEditWarn()) {
        this.saveMultiSelect();
        ({start, end} = this.sortedMultiRange()!);
        this.deleteRangeHandler({
          start,
          end,
        });
      }
    } else if (keyboard.isUpKey(e)) {
      e.preventDefault();
      targetIndex = this.previousIndex(this.state.multiEndIndex as TIndex);
      if (e.shiftKey) {
        this.setState({multiEndIndex: targetIndex});
      } else {
        this.setMultiIndices({
          start: targetIndex,
          end: targetIndex,
        });
      }
    } else if (keyboard.isDownKey(e)) {
      e.preventDefault();
      targetIndex = this.nextIndex(this.state.multiEndIndex as TIndex);
      if (e.shiftKey) {
        this.setState({multiEndIndex: targetIndex});
      } else {
        this.setMultiIndices({
          start: targetIndex,
          end: targetIndex,
        });
      }
    } else if (keyboard.isCtrlA(e)) {
      e.preventDefault();
      this.setMultiIndices({
        start: this.firstIndex(),
        end: this.lastIndex(),
      });
    } else if (keyboard.isEnterKey(e)) {
      ({start, end} = this.sortedMultiRange()!);
      if (start === end && !this.isLockedWithEditWarn()) {
        // If only 1 item is selected, insert item below (or above if shift is pressed) instead of
        // replacing it. Shift+Enter at an entry-day boundary keeps the new NotePart on the current
        // day (via insertAtBoundaryStartsNextDay) rather than bumping it to the previous day; use the
        // shared helper so this policy stays in one place (CUST-12203).
        e.preventDefault();
        // insertBlockItemOnEnterKey expects `TIndex` to be `number`; however, in table situations, `TIndex`
        // is [number, number]`. We do not expect to reach this block in table situations, so we are casting.
        insertBlockItemOnEnterKey(
          this.insertItemHandler as (
            index: number,
            type?: string | null,
            options?: ItemInsertOptions
          ) => void,
          start as number,
          e
        );
      }
    } else {
      const format = formatters.formatTypeFromKeydown(e, {allowLink: false});
      if (format === FormattersConstants.SYMBOL) {
        // Can't insert symbol with multiple items selected
        return;
      }
      if (format != null) {
        e.preventDefault();
        if (this.formatRangeHandler != null && !this.isLockedWithEditWarn()) {
          this.saveMultiSelect();
          ({start, end} = this.sortedMultiRange()!);
          this.formatRangeHandler({start, end, format});
        }
      }
    }
  },
});

export default MultiSelectLinearList;
